import { useContext, useEffect, useState } from "react";
import { Coding } from "../../models/structuration";
import { TerminologyQuartzClientContext } from "../services/api/contexts";

/**
 * Hook to fetch and cache codings from a specific valueset
 */
export const useValuesetCodings = (valuesetName: string) => {
  const [codings, setCodings] = useState<Coding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useContext(TerminologyQuartzClientContext);

  useEffect(() => {
    const fetchCodings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch codings from the terminology service
        const fetchedCodings: Coding[] = await client.listCodings(valuesetName);
        setCodings(fetchedCodings);
      } catch (err) {
        console.error(`Failed to fetch ${valuesetName} codings:`, err);
        setError(`Failed to load ${valuesetName} data`);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if client is available and valuesetName is provided
    if (client && valuesetName) {
      fetchCodings();
    }
  }, [client, valuesetName]);

  /**
   * Get display value for a code
   * Falls back to the code itself if no display value is found
   */
  const getDisplayForCode = (code: string | undefined | null): string => {
    if (!code) return "-";

    // If we're still loading and have no data, show the code
    if (loading && codings.length === 0) {
      return code;
    }

    // Find the coding and return display or fallback to code
    const coding = codings.find((c) => c.code === code);
    return coding?.display || code;
  };

  return {
    codings,
    loading,
    error,
    getDisplayForCode,
  };
};
